import clsx from 'clsx';
import type { ComponentProps, FunctionComponent, ReactElement, ReactNode } from 'react';

import { useKeyboardShortcutsEnabled } from '../keyboard-shortcuts/KeyboardShortcutsContext';
import { Tooltip } from '../tooltip/Tooltip';

export type ShortcutSequence = {
    readonly keys: readonly string[];
    readonly separator?: 'plus' | 'then';
};

export type ShortcutTooltipProps = {
    readonly align?: ComponentProps<typeof Tooltip.Content>['align'];
    readonly alignOffset?: number;
    readonly children: ReactElement;
    readonly className?: string;
    readonly enabled?: boolean;
    readonly label: ReactNode;
    readonly shortcuts: readonly ShortcutSequence[];
    readonly side?: ComponentProps<typeof Tooltip.Content>['side'];
    readonly sideOffset?: number;
};

const KeyCap: FunctionComponent<{ readonly children: string }> = ({ children }) => (
    <kbd className="inline-flex h-4 min-w-4 items-center justify-center rounded-xs border border-gray-600 bg-gray-800 px-xxs font-sans text-xxxs leading-none text-white-200">
        {children}
    </kbd>
);

const ShortcutKeys: FunctionComponent<{ readonly shortcut: ShortcutSequence }> = ({ shortcut }) => {
    const separator = shortcut.separator === 'then' ? '→' : '+';

    return (
        <span className="flex items-center gap-xs">
            {shortcut.keys.map((key, index) => (
                <span key={key} className="contents">
                    {index > 0 && <span aria-hidden={true}>{separator}</span>}
                    <KeyCap>{key}</KeyCap>
                </span>
            ))}
        </span>
    );
};

export const ShortcutTooltip: FunctionComponent<ShortcutTooltipProps> = ({
    align,
    alignOffset,
    children,
    className,
    enabled = true,
    label,
    shortcuts,
    side,
    sideOffset = 6,
}) => {
    const keyboardShortcutsEnabled = useKeyboardShortcutsEnabled();

    if (!keyboardShortcutsEnabled || !enabled || shortcuts.length === 0) {
        return children;
    }

    return (
        <Tooltip>
            <Tooltip.Trigger asChild={true}>{children}</Tooltip.Trigger>
            <Tooltip.Portal>
                <Tooltip.Content
                    align={align}
                    alignOffset={alignOffset}
                    className={clsx('flex items-center gap-s text-xxxs', className)}
                    side={side}
                    sideOffset={sideOffset}
                >
                    <span>{label}</span>
                    <span className="flex items-center gap-xs">
                        {shortcuts.map((shortcut, index) => (
                            <span key={shortcut.keys.join('-')} className="contents">
                                {index > 0 && <span aria-hidden={true}>/</span>}
                                <ShortcutKeys shortcut={shortcut} />
                            </span>
                        ))}
                    </span>
                </Tooltip.Content>
            </Tooltip.Portal>
        </Tooltip>
    );
};
